import { type RootState } from '@/store';
import { type UnknownAction } from '@reduxjs/toolkit';
import {
  type FileStatus,
  type SelectedFile,
  type TaskIdStatus,
  type WebsocketFileDownload,
} from 'components/DynamicDownload/interfaces';

enum DownloadActionType {
  ADD_DOWNLOAD_FILE = 'download/add_download_file',
  UPDATE_DOWNLOAD_FILE_STATUS = 'download/update_download_file_status',
  UPDATE_DOWNLOAD_ERROR_FILE = 'download/update_download_error_file_status',
  CLEAR_DOWNLOAD_LIST = 'download/clear_download_list',
  SET_IS_GENERATING_FILE = 'download/set_is_generating_file',
  SET_WEBSOCKET_FILE = 'download/set_websocket_file',
  SET_TASK_IDS = 'download/set_task_ids',
  UPDATE_TASK_ID = 'download/update_task_id',
  REMOVE_TASK_ID = 'download/remove_task_id',
}

export const addDownloadFile = (file: SelectedFile) => ({
  type: DownloadActionType.ADD_DOWNLOAD_FILE,
  payload: file,
});

export const updateDownloadFileStatus = (fileId: number | string, status: FileStatus) => ({
  type: DownloadActionType.UPDATE_DOWNLOAD_FILE_STATUS,
  payload: { fileId, status },
});

export const updateDownloadErrorFile = (fileId: number | string) => ({
  type: DownloadActionType.UPDATE_DOWNLOAD_ERROR_FILE,
  payload: { fileId },
});

export const clearDownloadList = () => ({
  type: DownloadActionType.CLEAR_DOWNLOAD_LIST,
});

export const setIsGeneratingFile = (isGeneratingFile: boolean) => ({
  type: DownloadActionType.SET_IS_GENERATING_FILE,
  payload: isGeneratingFile,
});

export const setWebsocketFile = (id: string, wsFileUrl: string) => ({
  type: DownloadActionType.SET_WEBSOCKET_FILE,
  payload: { id, wsFileUrl },
});

export const setTaskId = (id: string, status: TaskIdStatus) => ({
  type: DownloadActionType.SET_TASK_IDS,
  payload: { [id]: status },
});

export const updateTaskId = (id: string, status: TaskIdStatus) => ({
  type: DownloadActionType.SET_TASK_IDS,
  payload: { [id]: status },
});

export const removeTaskId = (id: string) => ({
  type: DownloadActionType.REMOVE_TASK_ID,
  payload: id,
});

interface DownloadState {
  downloadList: SelectedFile[];
  isGeneratingFile: boolean;
  websocketFileList: WebsocketFileDownload[];
  taskIds: Record<string, TaskIdStatus>;
}

const INITIAL_STATE: DownloadState = {
  downloadList: [],
  isGeneratingFile: false,
  websocketFileList: [],
  taskIds: {},
};

export default function downloadsReducer(
  state = INITIAL_STATE,
  action: UnknownAction
): DownloadState {
  const { type, payload } = action;

  switch (type) {
    case DownloadActionType.ADD_DOWNLOAD_FILE: {
      const downloadList = [...state.downloadList, payload] as SelectedFile[];

      return {
        ...state,
        downloadList,
      };
    }

    case DownloadActionType.UPDATE_DOWNLOAD_FILE_STATUS: {
      const p1 = payload as { fileId: number; status: FileStatus };
      return {
        ...state,
        downloadList: state.downloadList.map((file) =>
          file.id === p1.fileId ? { ...file, status: p1.status } : file
        ),
      };
    }

    case DownloadActionType.UPDATE_DOWNLOAD_ERROR_FILE: {
      const p2 = payload as { fileId: number; status: FileStatus };
      return {
        ...state,
        downloadList: state.downloadList.filter((file) => file.id !== p2.fileId),
      };
    }

    case DownloadActionType.CLEAR_DOWNLOAD_LIST:
      return {
        ...state,
        downloadList: [],
        websocketFileList: [],
        taskIds: {},
      };

    case DownloadActionType.SET_IS_GENERATING_FILE:
      return {
        ...state,
        isGeneratingFile: payload as boolean,
      };

    case DownloadActionType.SET_WEBSOCKET_FILE: {
      const websocketFileList = [...state.websocketFileList, payload] as WebsocketFileDownload[];

      return {
        ...state,
        websocketFileList,
      };
    }

    case DownloadActionType.SET_TASK_IDS:
      return {
        ...state,
        taskIds: {
          ...state.taskIds,
          ...(payload as Record<string, TaskIdStatus>),
        },
      };

    case DownloadActionType.UPDATE_TASK_ID: {
      const p3 = payload as { id: string; status: TaskIdStatus };
      return {
        ...state,
        taskIds: {
          ...state.taskIds,
          [p3.id]: p3.status,
        },
      };
    }

    case DownloadActionType.REMOVE_TASK_ID: {
      const newTaskIds = { ...state.taskIds };
      delete newTaskIds[action.payload as number];
      return {
        ...state,
        taskIds: newTaskIds,
      };
    }

    default:
      return state;
  }
}

export const selectDownloadList = (state: RootState) => state.downloads.downloadList;
export const selectIsGeneratingFile = (state: RootState) => state.downloads.isGeneratingFile;
export const selectWebsocketFileList = (state: RootState) => state.downloads.websocketFileList;
export const selectTaskIds = (state: RootState) => state.downloads.taskIds;
